import {
  Activity,
  Ban,
  Check,
  CircleAlert,
  Link2,
  LockKeyhole,
  ShieldCheck,
  Wifi,
  RefreshCw,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {listWorkspaceApps,listWorkspaceProviders,type AppConnection,type CatalogProvider} from '../lib/agentordinaryApps';
import {COMPANY_BRANDS,findCompanyBrand} from '../lib/companyBrands';
import CompanyMark from './CompanyMark';
import './AgentConnections.css';
import {
  getSafetyState,
  listApps,
  listModelProviders,
  listResponsibilities,
  listTasks,
  listToolProviders,
  pauseResponsibility,
  resumeResponsibility,
  setGlobalRuntimeState,
  setResponsibilityPriority,
} from '../lib/agentopia';
import type {
  Agent,
  AgentopiaApp,
  AgentResponsibility,
  AgentTask,
  ModelProvider,
  SafetyState,
  ToolProvider,
} from '../types';

const issueMessage = (problem: unknown) =>
  problem instanceof Error ? problem.message : 'AgentOrdinary data could not be loaded.';

export function LiveWork({
  workspaceId,
  agents,
}: {
  workspaceId: string;
  agents: Agent[];
}) {
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [workFilter, setWorkFilter] = useState<'all' | 'active' | 'blocked' | 'submitted' | 'stopped'>('all');
  const [visibleCount, setVisibleCount] = useState(5);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [responsibilities, setResponsibilities] = useState<AgentResponsibility[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyResponsibilityId, setBusyResponsibilityId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([listTasks(workspaceId), listResponsibilities(workspaceId)])
      .then(([items, ownedResponsibilities]) => {
        if (!active) return;
        setTasks(items);
        setResponsibilities(ownedResponsibilities);
        setLastRefreshed(new Date());
      })
      .catch(problem => {
        if (active) setError(issueMessage(problem));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workspaceId]);

  const names = useMemo(
    () => new Map(agents.map(agent => [agent.id, agent.nickname || agent.official_name])),
    [agents],
  );
  // Recorded states are not proof of delivery, acceptance or payment.
  // A blocked item only needs the owner when an explicit action is recorded.
  const active = tasks.filter(task =>
    ['queued', 'claimed', 'working', 'running'].includes(task.status.toLowerCase()),
  );
  const needsYou = tasks.filter(task => task.requires_approval);
  const blocked = tasks.filter(task => task.status.toLowerCase() === 'blocked');
  const submitted = tasks.filter(task => ['submitted', 'completed', 'done'].includes(task.status.toLowerCase()));
  const stopped = tasks.filter(task => ['stopped', 'cancelled'].includes(task.status.toLowerCase()));
  const filteredTasks = tasks.filter(task => {
    const status = task.status.toLowerCase();
    if (workFilter === 'active') return ['queued', 'claimed', 'working', 'running', 'waiting'].includes(status);
    if (workFilter === 'blocked') return status === 'blocked';
    if (workFilter === 'submitted') return ['submitted', 'completed', 'done'].includes(status);
    if (workFilter === 'stopped') return ['stopped', 'cancelled'].includes(status);
    return true;
  });
  const taskFilters = [
    { key: 'all', label: 'All', count: tasks.length },
    { key: 'active', label: 'Active', count: active.length + tasks.filter(task => task.status.toLowerCase() === 'waiting').length },
    { key: 'blocked', label: 'Blocked', count: blocked.length },
    { key: 'submitted', label: 'Submitted', count: submitted.length },
    { key: 'stopped', label: 'Stopped', count: stopped.length },
  ] as const;
  const liveResponsibilities = responsibilities.filter(item =>
    ['active', 'working', 'waiting', 'blocked', 'needs_owner', 'paused'].includes(item.status),
  );

  async function refreshWork() {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const [items, ownedResponsibilities] = await Promise.all([
        listTasks(workspaceId), listResponsibilities(workspaceId),
      ]);
      setTasks(items);
      setResponsibilities(ownedResponsibilities);
      setLastRefreshed(new Date());
    } catch (problem) {
      setError(issueMessage(problem));
    } finally {
      setLoading(false);
    }
  }

  async function refreshResponsibilities() {
    setResponsibilities(await listResponsibilities(workspaceId));
  }

  async function changeResponsibilityState(item: AgentResponsibility) {
    if (busyResponsibilityId) return;
    setBusyResponsibilityId(item.id);
    setError('');
    try {
      if (item.status === 'paused') {
        await resumeResponsibility(item.id);
      } else {
        await pauseResponsibility(item.id);
      }
      await refreshResponsibilities();
      setTasks(await listTasks(workspaceId));
    } catch (problem) {
      setError(issueMessage(problem));
    } finally {
      setBusyResponsibilityId('');
    }
  }

  async function nudgePriority(item: AgentResponsibility, delta: number) {
    if (busyResponsibilityId) return;
    setBusyResponsibilityId(item.id);
    setError('');
    try {
      await setResponsibilityPriority(item.id, item.priority + delta);
      await refreshResponsibilities();
    } catch (problem) {
      setError(issueMessage(problem));
    } finally {
      setBusyResponsibilityId('');
    }
  }

  return (
    <div className="page-stack">
      <section className="ops-hero blue-hero">
        <div>
          <div className="eyebrow">LIVE WORK</div>
          <h1>See the work itself, not agent chatter.</h1>
          <p>
            See recorded work states, blockers and available results. Submitted work is not automatically verified or paid.
          </p>
        </div>
        <div className="hero-stat">
          <strong>{active.length}</strong>
          <span>active work</span>
        </div>
        <div className="hero-stat teal-stat">
          <strong>{needsYou.length}</strong>
          <span>need you</span>
        </div>
      </section>

      {error && <div className="message error"><CircleAlert size={17} />{error}</div>}

      <section className="panel">
        <div className="title-row">
          <div>
            <h2>Persistent responsibilities</h2>
            <p className="muted">
              These jobs continue across chats, waits, handoffs, and scheduled wakeups.
            </p>
          </div>
          <span className="count">{loading ? 'Loading' : liveResponsibilities.length}</span>
        </div>
        {liveResponsibilities.length === 0 && !loading ? (
          <div className="empty">No active persistent responsibilities.</div>
        ) : (
          <div className="task-list">
            {liveResponsibilities.map(item => (
              <ResponsibilityCard
                key={item.id}
                item={item}
                agentName={names.get(item.agent_id)}
                busy={busyResponsibilityId === item.id}
                onToggle={() => void changeResponsibilityState(item)}
                onRaise={() => void nudgePriority(item, 10)}
                onLower={() => void nudgePriority(item, -10)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="panel">
        <div className="title-row">
          <h2>Action needed</h2>
          <span className="count">{needsYou.length}</span>
        </div>
        {needsYou.length === 0 ? (
          <div className="empty">Nothing currently requires owner action.</div>
        ) : (
          <div className="task-list">
            {needsYou.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                agentName={task.agent_id ? names.get(task.agent_id) : undefined}
              />
            ))}
          </div>
        )}
      </section>

      <section className="panel">
        <div className="title-row">
          <div>
            <h2>Recorded work</h2>
            <p className="muted">Actual saved work records. Expand an item to inspect what was recorded.</p>
          </div>
          <button className="outline-action" type="button" disabled={loading} onClick={() => void refreshWork()}>
            <RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
        <p className="work-updated">
          {lastRefreshed ? `Refreshed ${lastRefreshed.toLocaleString()}` : 'Loading the work records'}
          {' · '}{tasks.length} recent records
        </p>
        <div className="work-filters" role="group" aria-label="Filter recorded work">
          {taskFilters.map(filter => (
            <button
              key={filter.key}
              type="button"
              className={workFilter === filter.key ? 'work-filter active' : 'work-filter'}
              aria-pressed={workFilter === filter.key}
              onClick={() => {
                setWorkFilter(filter.key);
                setVisibleCount(5);
              }}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
        {filteredTasks.length === 0 && !loading ? (
          <div className="empty">No work records match this filter.</div>
        ) : (
          <div className="task-list">
            {filteredTasks.slice(0, visibleCount).map(task => (
              <TaskCard
                key={task.id}
                task={task}
                agentName={task.agent_id ? names.get(task.agent_id) : undefined}
              />
            ))}
            {filteredTasks.length > visibleCount && (
              <button
                className="outline-action work-load-more"
                type="button"
                onClick={() => setVisibleCount(current => current + 5)}
              >
                Show 5 more ({filteredTasks.length - visibleCount} remaining)
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function ResponsibilityCard({
  item,
  agentName,
  busy,
  onToggle,
  onRaise,
  onLower,
}: {
  item: AgentResponsibility;
  agentName?: string;
  busy: boolean;
  onToggle: () => void;
  onRaise: () => void;
  onLower: () => void;
}) {
  const next = item.next_action || item.resume_condition || 'Waiting for the next valid event or checkpoint.';
  return (
    <article className="task-card">
      <div className="task-card-head">
        <div>
          <strong>{item.name}</strong>
          <small>{agentName || 'Assigned employee'} · {item.status} · {item.mode}</small>
        </div>
        <span className="count">{'Priority ' + item.priority}</span>
      </div>
      <p>{item.objective}</p>
      {item.current_action && (
        <div className="task-meta">
          <span><strong>Now:</strong> {item.current_action}</span>
        </div>
      )}
      <div className="task-meta">
        <span><strong>Next:</strong> {next}</span>
      </div>
      {item.checkpoint && (
        <div className="task-meta">
          <span><strong>Checkpoint:</strong> {item.checkpoint}</span>
          {item.next_review_at && <span>{'Next wake ' + new Date(item.next_review_at).toLocaleString()}</span>}
        </div>
      )}
      <div className="save-controls">
        <button className="outline-action" disabled={busy} onClick={onLower}>Lower priority</button>
        <button className="outline-action" disabled={busy} onClick={onRaise}>Raise priority</button>
        <button className="primary-action" disabled={busy} onClick={onToggle}>
          {busy ? 'Updating...' : item.status === 'paused' ? 'Resume' : 'Pause'}
        </button>
      </div>
    </article>
  );
}

function TaskCard({
  task,
  agentName,
}: {
  task: AgentTask;
  agentName?: string;
}) {
  const metadata = task.metadata || {};
  const rawResult = metadata.result ?? metadata.workProduct ?? metadata.execution_result ?? null;
  const hasResult = rawResult !== null && rawResult !== undefined;
  const resultText = hasResult
    ? (typeof rawResult === 'string' ? rawResult : JSON.stringify(rawResult, null, 2)).slice(0, 3000)
    : '';
  const external = typeof metadata.external_url === 'string' ? metadata.external_url.trim() : '';
  const validLink = /^https:\/\/[^\s]+$/i.test(external) ? external : '';
  const verification = metadata.outcomeVerification && typeof metadata.outcomeVerification === 'object'
    ? metadata.outcomeVerification as Record<string, unknown>
    : null;
  return (
    <article className="task-card">
      <div className="task-card-head">
        <div>
          <strong>{task.title}</strong>
          <small>{agentName || 'Unassigned'} · {task.status}</small>
        </div>
        <span className="count">{'Priority ' + task.priority}</span>
      </div>
      {task.objective && <p>{task.objective}</p>}
      {task.blocker && <div className="task-blocker"><Ban size={15} />{task.blocker}</div>}
      <div className="task-meta">
        {task.requires_approval && <span>Needs your approval</span>}
        <span>{new Date(task.updated_at).toLocaleString()}</span>
      </div>
      <details className="task-details">
        <summary>View record details</summary>
        <div className="task-proof">
          <div><strong>Recorded state:</strong> {task.status}</div>
          <div><strong>Source:</strong> {task.source_system || 'Not recorded'}</div>
          {task.last_error && <div className="task-blocker"><Ban size={14} />{task.last_error}</div>}
          {typeof metadata.blocked_reason === 'string' && (
            <div className="task-blocker"><Ban size={14} />{metadata.blocked_reason}</div>
          )}
          {verification && (
            <div><strong>Outcome verification:</strong> {String(verification.status || 'Not verified')}</div>
          )}
          {validLink && (
            <a href={validLink} target="_blank" rel="noreferrer noopener">Open original source</a>
          )}
          {hasResult ? (
            <div>
              <strong>Recorded result (not independently verified)</strong>
              <pre className="task-result">{resultText}</pre>
            </div>
          ) : (
            <div className="muted">No deliverable or verification receipt is present in this work record.</div>
          )}
          {['submitted', 'completed', 'done'].includes(task.status.toLowerCase()) && (
            <div className="muted">This state does not confirm customer acceptance or payment.</div>
          )}
        </div>
      </details>
    </article>
  );
}

export function Connections({ workspaceId,previewOnly=false }: { workspaceId: string;previewOnly?:boolean }) {
  const [apps,setApps]=useState<AppConnection[]>([]);
  const [providers,setProviders]=useState<CatalogProvider[]>([]);
  const [loading,setLoading]=useState(!previewOnly);
  const [error,setError]=useState('');
  const [revision,setRevision]=useState(0);
  useEffect(()=>{
    if(previewOnly){
      setApps(COMPANY_BRANDS.map(brand=>({
        key:brand.key,name:brand.name,kind:brand.category,status:'not_connected',
        registered:false,assigned:false,syncStatus:'not_assigned'
      })));
      setProviders([]);setLoading(false);setError('');return;
    }
    let live=true;setLoading(true);setError('');
    Promise.all([listWorkspaceApps(workspaceId),listWorkspaceProviders(workspaceId)]).then(([applicationRows,providerRows])=>{
      if(!live)return;setApps(applicationRows);setProviders(providerRows);
    }).catch(problem=>{if(live)setError(issueMessage(problem))}).finally(()=>{if(live)setLoading(false)});
    return()=>{live=false};
  },[workspaceId,revision,previewOnly]);
  const tools=providers.filter(x=>x.kind==='tool'),models=providers.filter(x=>x.kind==='model');
  return <section className="ao-connections-page" aria-label="AgentOrdinary connections">
    <header className="ao-connections-head"><div className="eyebrow">AGENTORDINARY · CONNECTIONS{previewOnly?' · PREVIEW':''}</div><div className="ao-connections-title-row">
      <div><h1>Your companies</h1><p>{previewOnly?'A preview of your company ecosystem. Brand cards do not establish connections.':'Your company ecosystem in one place. Application registrations, employee assignments and live connectors are separate. Nothing is activated here.'}</p></div>
      <button type="button" onClick={()=>setRevision(x=>x+1)} disabled={loading||previewOnly}><RefreshCw size={17}/>Refresh</button>
    </div></header>
    {loading&&<p role="status" className="ao-connections-notice">Checking your workspace connections…</p>}
    {error&&<p role="alert" className="ao-connections-notice ao-connections-error">{error}</p>}
    {!loading&&!error&&<>
      <section className="ao-connections-panel">
        <div className="ao-connections-section-title"><div><h2>Applications</h2><p>Existing and planned apps. Open an employee’s Connected Apps tab to see their actual assignments.</p></div><span>{apps.length}</span></div>
        <div className="ao-connections-grid">{apps.map(app=><article className="ao-connections-tile" key={app.key}>
          <CompanyMark brandKey={app.key} name={app.name}/>
          <strong>{app.name}</strong>
          <small className="ao-connection-category">{findCompanyBrand(app.key)?.category||app.kind}</small>
          <small className="ao-connection-status">{app.registered?'Registered · '+app.status:'Connector pending'}{app.key==='woven'?' · Logo pending':''}</small>
        </article>)}</div>
      </section>
      <section className="ao-connections-panel">
        <div className="ao-connections-section-title"><div><h2>Tool providers</h2><p>Availability and health reported by the workspace. Disabled providers cannot be used.</p></div><span>{tools.length}</span></div>
        <div className="ao-connections-grid">{tools.map(tool=><article className="ao-connections-tile" key={tool.id}>
         <span className="ab4-app-icon" aria-hidden="true">{tool.name.slice(0,1).toUpperCase()}</span><strong>{tool.name}</strong><small>{tool.enabled?'Enabled · '+tool.status:'Disabled'} · {tool.freeOnly?'Free only':'Paid access possible'}</small>
        </article>)}</div>
      </section>
      <section className="ao-connections-panel">
        <div className="ao-connections-section-title"><div><h2>AI model providers</h2><p>These are model providers, not employee skills or application connections.</p></div><span>{models.length}</span></div>
        <div className="ao-connections-grid">{models.map(model=><article className="ao-connections-tile" key={model.id}>
         <span className="ab4-app-icon" aria-hidden="true">{model.name.slice(0,1).toUpperCase()}</span><strong>{model.name}</strong><small>{model.enabled?'Enabled · '+model.status:'Disabled'} · {model.freeOnly?'Free only':'Paid access possible'}</small>
        </article>)}</div>
      </section>
    </>}
  </section>;
}

function StatusCard({
  name,
  state,
  note,
}: {
  name: string;
  state: string;
  note: string;
}) {
  return (
    <article className="status-card">
      <div className="status-icon"><Wifi size={17} /></div>
      <div>
        <strong>{name}</strong>
        <small>{note}</small>
      </div>
      <span className="count">{state}</span>
    </article>
  );
}

export function Guardrail({ workspaceId }: { workspaceId: string }) {
  const [state, setState] = useState<SafetyState | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    getSafetyState(workspaceId)
      .then(next => {
        if (active) setState(next);
      })
      .catch(problem => {
        if (active) setError(issueMessage(problem));
      });
    return () => {
      active = false;
    };
  }, [workspaceId]);

  async function toggleGlobalState() {
    if (!state || busy) return;
    setBusy(true);
    setError('');
    try {
      const nextState = state.global_paused ? 'active' : 'paused';
      await setGlobalRuntimeState(
        nextState,
        nextState === 'paused'
          ? 'Paused by owner from AgentOrdinary Guardrail.'
          : 'Resumed by owner from AgentOrdinary Guardrail.',
      );
      setState(await getSafetyState(workspaceId));
    } catch (problem) {
      setError(issueMessage(problem));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <section className="ops-hero coral-hero">
        <div>
          <div className="eyebrow">SAFETY</div>
          <h1>{state?.global_paused ? 'Workforce is paused.' : 'Workforce is active.'}</h1>
          <p>
            {state?.global_paused
              ? 'Canonical execution is blocked until you resume the workforce.'
              : 'Phase B activation policy is live. Safety limits remain armed while employees work.'}
          </p>
        </div>
        <LockKeyhole size={36} />
      </section>

      {error && <div className="message error"><CircleAlert size={17} />{error}</div>}

      {state && (
        <>
          <section className="panel guardrail-state">
            <div className="title-row">
              <h2>Current safety state</h2>
              <span className={state.global_paused ? 'lock-chip' : 'warning-chip'}>
                {state.global_paused ? 'GLOBAL PAUSE ON' : 'GLOBAL PAUSE OFF'}
              </span>
            </div>
            <div className="guardrail-grid">
              <Metric label="Safety armed" value={state.armed ? 'Yes' : 'No'} />
              <Metric
                label="Stop on hard errors"
                value={state.hard_error_immediate ? 'Yes' : 'No'}
              />
              <Metric
                label="Failure limit"
                value={String(state.max_identical_failures)}
              />
              <Metric
                label="Test limit"
                value={String(state.max_identical_tests)}
              />
            </div>
            <div className="pause-reason">
              <strong>Owner hold</strong>
              <p>{state.pause_reason || 'No pause reason stored.'}</p>
            </div>
          </section>

          <section className="panel">
            <div className="title-row">
              <div>
                <h2>Runtime controls</h2>
                <p className="muted">Global pause is immediate at the canonical SALRI capability boundary.</p>
              </div>
              <button className={state.global_paused ? 'primary-action' : 'outline-action'} disabled={busy} onClick={() => void toggleGlobalState()}>
                {busy ? 'Updating...' : state.global_paused ? 'Resume workforce' : 'Pause workforce'}
              </button>
            </div>
            <div className="gate-list">
              <Gate done={state.armed} text="Safety guardrail is armed." />
              <Gate done={state.max_identical_failures > 0} text={'Repeated-failure limit is set to ' + state.max_identical_failures + '.'} />
              <Gate done={state.max_identical_tests > 0} text={'Runaway-test limit is set to ' + state.max_identical_tests + '.'} />
              <Gate done={true} text="Paid AI remains locked behind explicit owner approval and non-zero caps." />
              <Gate done={true} text="Per-employee pause and responsibility priority controls are available in Live Work." />
              <Gate done={!state.global_paused} text="Canonical workforce activation is currently enabled." />
            </div>
            <div className="runtime-lock">
              <ShieldCheck size={18} />
              <div>
                <strong>{state.global_paused ? 'Execution is blocked' : 'Execution is policy-governed'}</strong>
                <span>
                  {state.global_paused
                    ? 'AgentOrdinary employees cannot use SALRI capabilities until you resume the workforce.'
                    : 'Employees may work only while both the global policy and their individual activation policy are active.'}
                </span>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Gate({ done, text }: { done: boolean; text: string }) {
  return (
    <div className={'gate-row ' + (done ? 'done' : '')}>
      {done ? <Check size={16} /> : <Activity size={16} />}
      <span>{text}</span>
    </div>
  );
}

